import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import AMPageShell from './components/AMPageShell';
import { ASSETS } from './data/assetData';
import Icon from 'components/AppIcon';

const SECTOR_COLOURS = {
  'Renewable Energy': '#0D9488',
  'Transport': '#3b4c4c',
  'Digital Infrastructure': '#8B5CF6',
  'Utilities': '#F59E0B',
};

const WATCHLIST_COLOURS = {
  'Confirmed': { bg: 'bg-green-100', text: 'text-green-700', dot: '#10B981' },
  'Confirmed with actions': { bg: 'bg-blue-100', text: 'text-blue-700', dot: '#3B82F6' },
  'Watchlist': { bg: 'bg-amber-100', text: 'text-amber-700', dot: '#F59E0B' },
  'Escalated': { bg: 'bg-red-100', text: 'text-red-700', dot: '#EF4444' },
  'Exit case': { bg: 'bg-purple-100', text: 'text-purple-700', dot: '#8B5CF6' },
};

const REVIEW_COLOURS = {
  'Reviewed': { bg: 'bg-green-100', text: 'text-green-700' },
  'Draft': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'Submitted': { bg: 'bg-blue-100', text: 'text-blue-700' },
};

const X_AXIS_OPTIONS = [
  { value: 'irrVsTarget', label: 'IRR vs Target' },
  { value: 'esgScore', label: 'ESG Score' },
  { value: 'riskScore', label: 'Risk Score' },
  { value: 'moic', label: 'MOIC' },
  { value: 'cashYield', label: 'Cash Yield' },
];

const Y_AXIS_OPTIONS = X_AXIS_OPTIONS;

const BUBBLE_SIZE_OPTIONS = [
  { value: 'fundExposure', label: 'Fund Exposure (£m)' },
  { value: 'commitment', label: 'Commitment' },
  { value: 'moic', label: 'MOIC' },
];

const COLOUR_BY_OPTIONS = [
  { value: 'sector', label: 'Sector' },
  { value: 'country', label: 'Country' },
  { value: 'stage', label: 'Stage' },
  { value: 'watchlistStatus', label: 'Watchlist Status' },
  { value: 'reviewStatus', label: 'Review Status' },
];

const COUNTRY_COLOURS = {
  'United Kingdom': '#3b4c4c',
  'Spain': '#EF4444',
  'Portugal': '#F59E0B',
  'Germany': '#10B981',
  'Sweden': '#8B5CF6',
};

const STAGE_COLOURS = {
  'Operational': '#10B981',
  'In Construction': '#F59E0B',
};

function getMetricValue(asset, metric) {
  switch (metric) {
    case 'irrVsTarget': return (asset?.irr ?? 0) - (asset?.irrTarget ?? 0);
    case 'esgScore': return asset?.esgScore ?? 0;
    case 'riskScore': return asset?.riskScore ?? 0;
    case 'moic': return asset?.multiple ?? 0;
    case 'cashYield': return asset?.cashYieldActual ?? 0;
    default: return 0;
  }
}

function getBubbleSize(asset, metric) {
  switch (metric) {
    case 'fundExposure': return asset?.fundExposure ?? 10;
    case 'commitment': return asset?.totalInvestmentValue ?? 10;
    case 'moic': return (asset?.multiple ?? 1) * 15;
    default: return asset?.fundExposure ?? 10;
  }
}

function getBubbleColour(asset, colourBy) {
  switch (colourBy) {
    case 'sector': return SECTOR_COLOURS?.[asset?.sector] || '#64748B';
    case 'country': return COUNTRY_COLOURS?.[asset?.country] || '#64748B';
    case 'stage': return STAGE_COLOURS?.[asset?.stage] || '#64748B';
    case 'watchlistStatus': return WATCHLIST_COLOURS?.[asset?.watchlistStatus]?.dot || '#64748B';
    case 'reviewStatus': return REVIEW_COLOURS?.[asset?.reviewStatus] ? '#10B981' : '#64748B';
    default: return SECTOR_COLOURS?.[asset?.sector] || '#64748B';
  }
}

function getIRRCellClass(val) {
  if (val > 0) return 'bg-green-50 text-green-700';
  if (val >= -1) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
}

function getESGCellClass(val) {
  if (val >= 75) return 'bg-green-50 text-green-700';
  if (val >= 60) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
}

function getRiskCellClass(val) {
  if (val <= 2) return 'bg-green-50 text-green-700';
  if (val === 3) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
}

function getMOICCellClass(val) {
  if (val >= 1.5) return 'bg-green-50 text-green-700';
  if (val >= 1.2) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
}

function getCashYieldCellClass(actual, budget) {
  if (actual >= budget) return 'bg-green-50 text-green-700';
  if (actual >= budget - 0.5) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload?.[0]?.payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm min-w-[200px]">
      <p className="font-semibold text-slate-900 mb-1">{d?.name}</p>
      <p className="text-slate-500 text-xs mb-2">{d?.sector} · {d?.country}</p>
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">X ({d?.xLabel}):</span>
          <span className="font-medium text-slate-800">{typeof d?.x === 'number' ? d?.x?.toFixed(2) : d?.x}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Y ({d?.yLabel}):</span>
          <span className="font-medium text-slate-800">{typeof d?.y === 'number' ? d?.y?.toFixed(2) : d?.y}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Fund Exposure:</span>
          <span className="font-medium text-slate-800">£{d?.fundExposure}m</span>
        </div>
      </div>
    </div>
  );
};

export default function PortfolioHeatmap({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const navigate = useNavigate();
  const [xAxis, setXAxis] = useState('irrVsTarget');
  const [yAxis, setYAxis] = useState('esgScore');
  const [bubbleSize, setBubbleSize] = useState('fundExposure');
  const [colourBy, setColourBy] = useState('sector');

  const scatterData = assets?.map((a) => ({
    ...a,
    x: getMetricValue(a, xAxis),
    y: getMetricValue(a, yAxis),
    z: getBubbleSize(a, bubbleSize),
    colour: getBubbleColour(a, colourBy),
    xLabel: X_AXIS_OPTIONS?.find((o) => o?.value === xAxis)?.label,
    yLabel: Y_AXIS_OPTIONS?.find((o) => o?.value === yAxis)?.label,
  }));

  const xThreshold = xAxis === 'irrVsTarget' ? 0 : xAxis === 'esgScore' ? 70 : null;
  const yThreshold = yAxis === 'esgScore' ? 70 : yAxis === 'irrVsTarget' ? 0 : null;

  const outperforming = assets?.filter((a) => (a?.irr ?? 0) > (a?.irrTarget ?? 0))?.length;
  const highESG = assets?.filter((a) => (a?.esgScore ?? 0) >= 75)?.length;
  const watchlisted = assets?.filter((a) => ['Watchlist', 'Escalated']?.includes(a?.watchlistStatus))?.length;

  const handleBubbleClick = useCallback((data) => {
    if (data?.id) navigate(`/asset-management/asset-detail/${data?.id}`);
  }, [navigate]);

  return (
    <AMPageShell activeTab="/asset-management/portfolio-heatmap">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem' }}>Portfolio Heatmap</h2>
          <p className="font-caption text-muted-foreground text-sm mt-0.5">At-a-glance performance and risk positioning across all assets — Sample data</p>
        </div>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card-interactive bg-card border border-border rounded-lg p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                <Icon name="TrendingUp" size={18} color="#10B981" />
              </div>
              <span className="font-caption text-sm text-muted-foreground">Outperforming IRR Target</span>
            </div>
            <p className="font-heading font-bold text-foreground" style={{ fontSize: '2rem' }}>{outperforming} <span className="text-base font-normal text-muted-foreground">of {assets?.length}</span></p>
          </div>
          <div className="card-interactive bg-card border border-border rounded-lg p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center">
                <Icon name="Leaf" size={18} color="#0D9488" />
              </div>
              <span className="font-caption text-sm text-muted-foreground">ESG Score ≥ 75</span>
            </div>
            <p className="font-heading font-bold text-foreground" style={{ fontSize: '2rem' }}>{highESG} <span className="text-base font-normal text-muted-foreground">of {assets?.length}</span></p>
          </div>
          <div className="card-interactive bg-card border border-border rounded-lg p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                <Icon name="AlertTriangle" size={18} color="#F59E0B" />
              </div>
              <span className="font-caption text-sm text-muted-foreground">On Watchlist or Escalated</span>
            </div>
            <p className="font-heading font-bold text-foreground" style={{ fontSize: '2rem' }}>{watchlisted} <span className="text-base font-normal text-muted-foreground">of {assets?.length}</span></p>
          </div>
        </div>

        {/* Axis Controls */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="font-caption text-xs text-muted-foreground font-medium uppercase tracking-wide">X-Axis</span>
              <select
                value={xAxis}
                onChange={(e) => setXAxis(e?.target?.value)}
                className="text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {X_AXIS_OPTIONS?.map((o) => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-caption text-xs text-muted-foreground font-medium uppercase tracking-wide">Y-Axis</span>
              <select
                value={yAxis}
                onChange={(e) => setYAxis(e?.target?.value)}
                className="text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {Y_AXIS_OPTIONS?.map((o) => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-caption text-xs text-muted-foreground font-medium uppercase tracking-wide">Bubble Size</span>
              <select
                value={bubbleSize}
                onChange={(e) => setBubbleSize(e?.target?.value)}
                className="text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {BUBBLE_SIZE_OPTIONS?.map((o) => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-caption text-xs text-muted-foreground font-medium uppercase tracking-wide">Colour By</span>
              <select
                value={colourBy}
                onChange={(e) => setColourBy(e?.target?.value)}
                className="text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {COLOUR_BY_OPTIONS?.map((o) => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Scatter Chart */}
        <div className="bg-card border border-border rounded-lg p-6 relative">
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
            {/* Quadrant labels */}
            <div className="absolute" style={{ top: '10%', right: '5%', zIndex: 2 }}>
              <span className="text-xs font-medium px-2 py-1 rounded bg-green-50 text-green-700 border border-green-200">High ESG / Outperforming</span>
            </div>
            <div className="absolute" style={{ top: '10%', left: '5%', zIndex: 2 }}>
              <span className="text-xs font-medium px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">High ESG / Underperforming</span>
            </div>
            <div className="absolute" style={{ bottom: '10%', right: '5%', zIndex: 2 }}>
              <span className="text-xs font-medium px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">Low ESG / Outperforming</span>
            </div>
            <div className="absolute" style={{ bottom: '10%', left: '5%', zIndex: 2 }}>
              <span className="text-xs font-medium px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200">Low ESG / Underperforming</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={480}>
            <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                type="number"
                dataKey="x"
                name={X_AXIS_OPTIONS?.find((o) => o?.value === xAxis)?.label}
                tick={{ fontSize: 11, fill: '#64748B' }}
                label={{ value: X_AXIS_OPTIONS?.find((o) => o?.value === xAxis)?.label, position: 'insideBottom', offset: -10, fontSize: 12, fill: '#64748B' }}
              />
              <YAxis
                type="number"
                dataKey="y"
                name={Y_AXIS_OPTIONS?.find((o) => o?.value === yAxis)?.label}
                tick={{ fontSize: 11, fill: '#64748B' }}
                label={{ value: Y_AXIS_OPTIONS?.find((o) => o?.value === yAxis)?.label, angle: -90, position: 'insideLeft', offset: 10, fontSize: 12, fill: '#64748B' }}
              />
              <Tooltip content={<CustomTooltip />} />
              {xThreshold !== null && (
                <ReferenceLine x={xThreshold} stroke="#94A3B8" strokeDasharray="4 4" strokeWidth={1.5} />
              )}
              {yThreshold !== null && (
                <ReferenceLine y={yThreshold} stroke="#94A3B8" strokeDasharray="4 4" strokeWidth={1.5} />
              )}
              <Scatter
                data={scatterData}
                onClick={(data) => handleBubbleClick(data)}
                style={{ cursor: 'pointer' }}
              >
                {scatterData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry?.colour}
                    fillOpacity={0.75}
                    stroke={entry?.colour}
                    strokeWidth={1.5}
                    r={Math.max(8, Math.min(28, (entry?.z / 5)))}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-2 justify-center">
            {colourBy === 'sector' && Object.entries(SECTOR_COLOURS)?.map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ background: v }} />
                <span className="text-xs text-muted-foreground">{k}</span>
              </div>
            ))}
            {colourBy === 'country' && Object.entries(COUNTRY_COLOURS)?.map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ background: v }} />
                <span className="text-xs text-muted-foreground">{k}</span>
              </div>
            ))}
            {colourBy === 'stage' && Object.entries(STAGE_COLOURS)?.map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ background: v }} />
                <span className="text-xs text-muted-foreground">{k}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Dimension Heatmap Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-heading font-semibold text-foreground text-base">Dimension Heatmap</h3>
            <p className="font-caption text-xs text-muted-foreground mt-0.5">Colour-coded performance matrix across all key dimensions</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-border">
                  <th className="text-left px-4 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Asset</th>
                  <th className="text-center px-3 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">IRR vs Target (Δ%)</th>
                  <th className="text-center px-3 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">ESG Score</th>
                  <th className="text-center px-3 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Risk Score</th>
                  <th className="text-center px-3 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">MOIC</th>
                  <th className="text-center px-3 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Cash Yield</th>
                  <th className="text-center px-3 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Watchlist</th>
                  <th className="text-center px-3 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Review</th>
                  <th className="text-center px-3 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide">View</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {assets?.map((asset) => {
                  const irrDelta = (asset?.irr ?? 0) - (asset?.irrTarget ?? 0);
                  const wlC = WATCHLIST_COLOURS?.[asset?.watchlistStatus] || { bg: 'bg-slate-100', text: 'text-slate-600' };
                  const rvC = REVIEW_COLOURS?.[asset?.reviewStatus] || { bg: 'bg-slate-100', text: 'text-slate-600' };
                  return (
                    <tr key={asset?.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-foreground text-sm">{asset?.name}</p>
                          <p className="text-xs text-muted-foreground">{asset?.sector}</p>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getIRRCellClass(irrDelta)}`}>
                          {irrDelta > 0 ? '+' : ''}{irrDelta?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getESGCellClass(asset?.esgScore ?? 0)}`}>
                          {asset?.esgScore ?? '—'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getRiskCellClass(asset?.riskScore ?? 0)}`}>
                          {asset?.riskScore ?? '—'}/5
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getMOICCellClass(asset?.multiple ?? 0)}`}>
                          {asset?.multiple?.toFixed(1) ?? '—'}×
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getCashYieldCellClass(asset?.cashYieldActual ?? 0, asset?.cashYieldBudget ?? 0)}`}>
                          {asset?.cashYieldActual?.toFixed(1) ?? '—'}%
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${wlC?.bg} ${wlC?.text}`}>
                          {asset?.watchlistStatus}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${rvC?.bg} ${rvC?.text}`}>
                          {asset?.reviewStatus}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button
                          onClick={() => navigate(`/asset-management/asset-detail/${asset?.id}`)}
                          className="text-primary text-xs font-medium hover:underline"
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AMPageShell>
  );
}
